/**
 * HTTP service for the project activity feed (PX-W6.3, TASK-EDD04F2F).
 *
 * Wraps `GET /{orgId}/projects/:projectId/activity` — cursor-paginated,
 * project-fenced, server-side-filtered (`limit`, `before`, `type`, `actorId`,
 * `entityType`). The feed never touches any unpaginated activity route.
 */

import { apiClient } from '@/services'
import type { ActivityFeedResponse } from '../types'
import { FEED_PAGE_SIZE } from '../types'

/** Arguments for a project-feed page fetch. */
export interface GetActivityFeedArgs {
  orgId: string
  /** Project slug (the URL `:projectId` token). */
  projectId: string
  /** Page size — the server rejects values outside 1..200. */
  limit?: number
  /** Cursor from a previous page's `nextCursor`; null/blank = first page. */
  before?: string | null
  /** Single activity-type filter. Null/blank = no clause. */
  type?: string | null
  /** Exact author id filter. Null/blank = no clause. */
  actorId?: string | null
  /** Entity-type filter (taskId|epicId|storyId|versionId|project). Null/blank = no clause. */
  entityType?: string | null
}

export const activityFeedService = {
  /** Fetch one page of the project activity feed (newest first). */
  async getFeed(args: GetActivityFeedArgs): Promise<ActivityFeedResponse> {
    const qs = new URLSearchParams({ limit: String(args.limit ?? FEED_PAGE_SIZE) })
    if (args.before) qs.set('before', args.before)
    if (args.type) qs.set('type', args.type)
    if (args.actorId) qs.set('actorId', args.actorId)
    if (args.entityType) qs.set('entityType', args.entityType)
    return apiClient.get<ActivityFeedResponse>(
      `/${args.orgId}/projects/${args.projectId}/activity?${qs.toString()}`
    )
  }
}
